import { FC, useState } from "react";
import { Alert, AlertTitle, Box, Button, Grid, Link, TextField, Typography } from "@mui/material";
import BusyIndicator from "../../components/BusyIndicator/BusyIndicator";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toastr } from "react-redux-toastr";
import { loginRequest } from "../../authConfig";
import { msalInstance } from "../../index";

interface SharepointUrlSearchProps {}

const getBearerHeader = async () => {

  const account = msalInstance.getActiveAccount();
  if (!account) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
  }

  const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account
  });

  return response.idToken; //accessToken;
};

const SharepointUrlSearch: FC<SharepointUrlSearchProps> = () => {
  const [sharepointFileUrl, setSharepointFileUrl] = useState<any>(undefined);
  const [searching, setSearching] = useState<any>(false);

  const fetchSharepointFilePath = async (googleFileUrl: string) => {

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/SharepointDocument/Search`;
    setSearching(true);
    setSharepointFileUrl(undefined);
    try {

      const accessToken = await getBearerHeader();

      var requestArgs = {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Accept: "application/json",
          "Authorization": `Bearer ${accessToken}`
        }),
        body: JSON.stringify({
          googleFileUrl,
        }),
      };
      const response = await fetch(url, requestArgs);
      if (!response.ok) {
        if (response.status === 404) {
          setSharepointFileUrl(null);
        } else {
          throw new Error(`Failed searching for file. ${response.status}`);
        }
      } else {
        var jsonResponse = await response.json();
        var newSharepointFileUrl = jsonResponse.sharepointFileUrl;
        setSharepointFileUrl(newSharepointFileUrl);
      }
    } finally {
      setSearching(false);
    }
  };

  const pathSuccessfullyFound = () =>
    sharepointFileUrl && (
      <Alert severity="success">
        <AlertTitle>Successfully Found</AlertTitle>
        <Link href={sharepointFileUrl} underline="always">
          {sharepointFileUrl}
        </Link>
      </Alert>
    );

  const pathNotFound = () => sharepointFileUrl === null && <Alert severity="error">No associated SharePoint URL found</Alert>;

  //const helpTextExampleUrl = `https://drive.google.com/file/d/1zkmbahyktriginv_nkaierapiev_h1in`;

  const labels = {
    googleFileUrl: "Google File URL",
  };

  const schema = Yup.object().shape({
    googleFileUrl: Yup.string().label(labels.googleFileUrl).required().max(1000),
  });

  const formik = useFormik({
    initialValues: { googleFileUrl: "" },
    onSubmit: async ({ googleFileUrl }) => {
      try {
        await fetchSharepointFilePath(googleFileUrl);
      } catch (err) {
        toastr.error("Error", "An unexpected error has occurred. Please try again a little later.");
      }
    },
    validationSchema: schema,
  });

  return (
    <Grid container flexDirection={"column"} rowSpacing={1}>
      <Grid item>
        <Grid item pb={1}>
          <Alert severity="info">
            <AlertTitle>Usage</AlertTitle>
            <Typography fontSize={13}>
              Enter the URL of the Google document you're wanting to find the associated SharePoint document URL for in the provided field and click the <b>Find</b> button.
            </Typography>
          </Alert>
        </Grid>

        <form onSubmit={formik.handleSubmit} style={{ flexGrow: 1 }}>
          <Grid container item xs flexDirection={{ xs: "column", md: "row" }} style={{ gap: "1rem" }}>
            <Grid item xs md>
              <TextField
                id="googleFileUrl"
                label={labels.googleFileUrl}
                variant="standard"
                autoComplete="off"
                error={formik.touched.googleFileUrl && Boolean(formik.errors.googleFileUrl)}
                helperText={formik.touched.googleFileUrl && formik.errors.googleFileUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.googleFileUrl}
                fullWidth
              />
            </Grid>
            <Grid item xs md={2} justifyContent={"flex-end"} sx={{ display: "flex" }}>
              <Box sx={{ width: 1 }}>
                <Button fullWidth variant="contained" type="submit" disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}>
                  <Box>Find</Box>
                  {searching && (
                    <Box alignItems={"baseline"} pl={0.5}>
                      <BusyIndicator />
                    </Box>
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* <pre>{JSON.stringify(formik, null, 2)}</pre> */}
        </form>
      </Grid>
      <Grid item>
        {pathSuccessfullyFound()}
        {pathNotFound()}
      </Grid>
    </Grid>
  );
};

export default SharepointUrlSearch;
